import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * RTK Query slice for the matching layer that connects ClientNeeds to
 * published ListingSheets via postal-code radius. Mirrors the response
 * shapes of `MatchingService.findListingsForNeed` /
 * `findNeedsForListing` on the backend.
 *
 * Used by the inline match sections inside ClientNeedStepCard
 * (matching listings) and ListingSheetStepCard (matching buyers).
 */

/**
 * A property in the buyer's postal-code radius. Either:
 *   - `isListed = true` with a populated `listing` (live, engaged seller)
 *   - `isListed = false` (suggested candidate; cold-outreach territory)
 *
 * The UI tags each card so a suggestion is never confused for a live
 * listing.
 */
export interface PropertyMatch {
  propertyId: string;
  property: {
    id: string;
    name: string | null;
    address: {
      addressLine1: string;
      city: string;
      state: string;
      postalCode: string;
    } | null;
    buildingSquareFeet: number | null;
    /** First PREVIEW asset's ref; render via `<DealImage assetRef={...}>`. */
    imageAssetRef: string | null;
  };
  isListed: boolean;
  listing: {
    listingSheetId: string;
    sellerRepProjectId: string;
    offering: string | null;
    askingPrice: string | null;
    askingLeaseRatePerSfPerYr: string | null;
    publishedAt: string;
  } | null;
  distanceMiles: number;
}

export interface NeedMatch {
  clientNeedId: string;
  buyerRepProjectId: string | null;
  person: { id: string; name: string } | null;
  title: string | null;
  lookingFor: string;
  budgetMin: string | null;
  budgetMax: string | null;
  sqftMin: number | null;
  sqftMax: number | null;
  postalCode: string;
  radius: number;
  status: string;
  distanceMiles: number;
}

export const matchingApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "matchingApi",
    baseUrl: "/api/matching",
  }),
  reducerPath: "matchingApi",
  tagTypes: ["PropertiesForNeed", "NeedsForListing"],
  endpoints: (builder) => ({
    getPropertiesForNeed: builder.query<{ data: PropertyMatch[] }, string>({
      query: (needId) => `/properties-for-need/${needId}`,
      providesTags: (_r, _e, needId) => [
        { type: "PropertiesForNeed", id: needId },
      ],
    }),
    getNeedsForListing: builder.query<{ data: NeedMatch[] }, string>({
      query: (sheetId) => `/needs-for-listing/${sheetId}`,
      providesTags: (_r, _e, sheetId) => [
        { type: "NeedsForListing", id: sheetId },
      ],
    }),
  }),
});

export const {
  useGetPropertiesForNeedQuery,
  useGetNeedsForListingQuery,
} = matchingApi;
